import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import DashboardContent from "../components/DashboardContent";
import { CsrAPI } from "../apis/CsrAPI";
import { API_URL } from "../utils/DomainUtils";
import triloLogo from "../assets/images/logos/TriloDocs-Logo-Horizontal-POSITIVE.png";
import "../scss/chat.scss";

function renderMarkdown(text) {
  const escape = s =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const inline = s =>
    escape(s)
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`(.+?)`/g, "<code>$1</code>")
      .replace(/\^([^\s^]+)\^?/g, "<sup>$1</sup>")
      .replace(/~([^\s~]+)~?/g, "<sub>$1</sub>");

  const isTableRow = l => /^\|.+\|$/.test(l.trim());
  const isSeparatorRow = l => /^\|[\s\-|:]+\|$/.test(l.trim());

  const isBlockStart = l =>
    /^#{1,4}\s/.test(l) ||
    /^---+$/.test(l.trim()) ||
    /^>\s?/.test(l) ||
    /^[-*]\s/.test(l) ||
    /^\d+\.\s/.test(l) ||
    isTableRow(l);

  const parseTableRow = l =>
    l
      .trim()
      .replace(/^\||\|$/g, "")
      .split("|")
      .map(c => c.trim());

  const lines = text.split("\n");
  const out = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^#{1,4}\s/.test(line)) {
      const level = line.match(/^(#{1,4})\s/)[1].length;
      out.push(
        `<h${level}>${inline(line.replace(/^#{1,4}\s+/, ""))}</h${level}>`
      );
      i++;
    } else if (/^---+$/.test(line.trim())) {
      out.push("<hr>");
      i++;
    } else if (isTableRow(line)) {
      // Collect all consecutive table lines
      const tableLines = [];
      while (i < lines.length && isTableRow(lines[i])) {
        tableLines.push(lines[i]);
        i++;
      }
      // First row = header, second row = separator (skip), rest = body
      const [headerRow, , ...bodyRows] = tableLines;
      const headers = parseTableRow(headerRow);
      const thead = `<thead><tr>${headers.map(h => `<th>${inline(h)}</th>`).join("")}</tr></thead>`;
      const tbody = bodyRows
        .filter(r => !isSeparatorRow(r))
        .map(
          r =>
            `<tr>${parseTableRow(r)
              .map(c => `<td>${inline(c)}</td>`)
              .join("")}</tr>`
        )
        .join("");
      out.push(`<table>${thead}<tbody>${tbody}</tbody></table>`);
    } else if (/^>\s?/.test(line) && line.trim() !== "") {
      const bqLines = [];
      while (
        i < lines.length &&
        /^>\s?/.test(lines[i]) &&
        lines[i].trim() !== ""
      ) {
        bqLines.push(inline(lines[i].replace(/^>\s?/, "")));
        i++;
      }
      out.push(`<blockquote><p>${bqLines.join("<br>")}</p></blockquote>`);
    } else if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^[-*]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(`<li>${inline(lines[i].replace(/^\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
    } else if (line.trim() === "") {
      i++;
    } else {
      const paraLines = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !isBlockStart(lines[i])
      ) {
        paraLines.push(inline(lines[i]));
        i++;
      }
      if (paraLines.length > 0) {
        out.push(`<p>${paraLines.join("<br>")}</p>`);
      } else {
        out.push(`<p>${inline(line)}</p>`);
        i++;
      }
    }
  }

  return out.join("\n");
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

async function buildPDFAssets(sessionDate, exportedAt, draftStates) {
  const { Document, Page, Text, View, Image, StyleSheet, pdf } =
    await import("@react-pdf/renderer");

  const TEAL = "#4ab8aa";
  const BLUE = "#474eff";
  const LINE = "#e5e7eb";

  const S = StyleSheet.create({
    page: {
      paddingTop: 60,
      paddingBottom: 56,
      paddingLeft: 50,
      paddingRight: 50,
      fontFamily: "Helvetica",
      fontSize: 10,
      color: "#1a1a2e",
      lineHeight: 1.55,
    },
    header: {
      position: "absolute",
      top: 16,
      left: 50,
      right: 50,
      flexDirection: "row",
      justifyContent: "flex-end",
      borderBottomWidth: 0.5,
      borderBottomColor: LINE,
      paddingBottom: 6,
    },
    logo: { height: 18, objectFit: "contain" },
    footer: {
      position: "absolute",
      bottom: 18,
      left: 50,
      right: 50,
      borderTopWidth: 0.5,
      borderTopColor: LINE,
      paddingTop: 4,
      flexDirection: "row",
      justifyContent: "space-between",
    },
    footerText: { fontSize: 7, color: "#9ca3af" },
    sectionLabel: {
      fontSize: 8,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      color: TEAL,
      letterSpacing: 1.2,
      marginTop: 22,
      marginBottom: 10,
    },
    draftCard: {
      borderWidth: 1,
      borderColor: TEAL,
      borderRadius: 4,
      padding: "10pt 14pt",
      marginBottom: 14,
      backgroundColor: "#f7fdfc",
    },
    draftTitle: {
      fontSize: 10,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      color: TEAL,
      marginBottom: 8,
    },
    userQ: {
      fontFamily: "Helvetica",
      fontWeight: "bold",
      color: BLUE,
      fontSize: 10,
      marginTop: 14,
      marginBottom: 3,
    },
    asstBlock: {
      borderLeftWidth: 2,
      borderLeftColor: LINE,
      paddingLeft: 10,
      marginBottom: 6,
    },
    h1: {
      fontSize: 14,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      marginTop: 10,
      marginBottom: 4,
    },
    h2: {
      fontSize: 12,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      marginTop: 8,
      marginBottom: 3,
    },
    h3: {
      fontSize: 11,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      marginTop: 6,
      marginBottom: 2,
    },
    h4: {
      fontSize: 10,
      fontFamily: "Helvetica",
      fontWeight: "bold",
      marginTop: 4,
      marginBottom: 2,
    },
    para: { marginBottom: 3 },
    li: { marginBottom: 2, paddingLeft: 10 },
    hr: {
      borderBottomWidth: 0.75,
      borderBottomColor: LINE,
      marginTop: 14,
      marginBottom: 14,
    },
    table: { marginTop: 8, marginBottom: 10 },
    tHeaderRow: {
      flexDirection: "row",
      backgroundColor: TEAL,
      borderWidth: 0.5,
      borderColor: "#3d9f93",
    },
    tRow: {
      flexDirection: "row",
      borderLeftWidth: 0.5,
      borderRightWidth: 0.5,
      borderBottomWidth: 0.5,
      borderColor: LINE,
    },
    tRowAlt: {
      flexDirection: "row",
      borderLeftWidth: 0.5,
      borderRightWidth: 0.5,
      borderBottomWidth: 0.5,
      borderColor: LINE,
      backgroundColor: "#f7fdfc",
    },
    tCell: {
      flex: 1,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
      paddingRight: 8,
      borderRightWidth: 0.5,
      borderRightColor: LINE,
    },
    tCellLast: {
      flex: 1,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
      paddingRight: 8,
    },
    tHeaderText: {
      fontFamily: "Helvetica",
      fontWeight: "bold",
      fontSize: 9,
      color: "#ffffff",
    },
    tCellText: { fontSize: 9 },
    draftRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6,
    },
    draftTagAccepted: { fontSize: 7, color: TEAL },
    draftTagEdited: { fontSize: 7, color: "#d97706" },
    draftContent: { fontSize: 9, lineHeight: 1.5, color: "#1a1a2e" },
  });

  // Inverse maps: Unicode super/subscript → ASCII, used in inlineRuns pre-processing.
  // Helvetica is an 8-bit Type 1 font; chars outside WinAnsiEncoding are mangled by PDFKit.
  const USUP_TO = {
    "⁰": "0",
    "¹": "1",
    "²": "2",
    "³": "3",
    "⁴": "4",
    "⁵": "5",
    "⁶": "6",
    "⁷": "7",
    "⁸": "8",
    "⁹": "9",
    "⁺": "+",
    "⁻": "-",
    "⁼": "=",
    "⁽": "(",
    "⁾": ")",
    ⁿ: "n",
    ⁱ: "i",
  };
  const USUB_TO = {
    "₀": "0",
    "₁": "1",
    "₂": "2",
    "₃": "3",
    "₄": "4",
    "₅": "5",
    "₆": "6",
    "₇": "7",
    "₈": "8",
    "₉": "9",
    "₊": "+",
    "₋": "-",
    "₌": "=",
    "₍": "(",
    "₎": ")",
  };
  const USUP_RE = /[⁰¹²³⁴⁵⁶⁷⁸⁹⁺⁻⁼⁽⁾ⁿⁱ]+/gu;
  const USUB_RE = /[₀₁₂₃₄₅₆₇₈₉₊₋₌₍₎]+/gu;

  function inlineRuns(raw) {
    const segments = [];
    let rest = raw
      .replace(/[✓✔☑✅]/g, "Yes")
      .replace(/[✗✘☒❌❎]/g, "No")
      // Convert Unicode super/subscript chars to ^..^ / ~..~ so the small-font
      // branch below handles them — they cannot render correctly in Helvetica.
      .replace(
        USUP_RE,
        m => "^" + [...m].map(c => USUP_TO[c] ?? c).join("") + "^"
      )
      .replace(
        USUB_RE,
        m => "~" + [...m].map(c => USUB_TO[c] ?? c).join("") + "~"
      );
    let k = 0;
    while (rest) {
      const bm = /\*\*(.+?)\*\*/.exec(rest);
      const im = /\*(.+?)\*/.exec(rest);
      const sm = /\^([^\s^]+)\^?/.exec(rest);
      const sbm = /~([^\s~]+)~?/.exec(rest);
      let pick = null;
      const candidates = [
        bm && { bold: true, m: bm },
        im && { italic: true, m: im },
        sm && { sup: true, m: sm },
        sbm && { sub: true, m: sbm },
      ].filter(Boolean);
      if (candidates.length)
        pick = candidates.reduce((a, b) => (a.m.index <= b.m.index ? a : b));

      if (!pick) {
        segments.push(React.createElement(Text, { key: k++ }, rest));
        break;
      }
      if (pick.m.index > 0)
        segments.push(
          React.createElement(Text, { key: k++ }, rest.slice(0, pick.m.index))
        );

      let style, content;
      if (pick.bold) {
        style = { fontFamily: "Helvetica", fontWeight: "bold" };
        content = pick.m[1];
      } else if (pick.italic) {
        style = { fontFamily: "Helvetica", fontStyle: "italic" };
        content = pick.m[1];
      } else if (pick.sup) {
        style = { fontSize: 6, verticalAlign: "super" };
        content = pick.m[1];
      } else {
        style = { fontSize: 6, verticalAlign: "sub" };
        content = pick.m[1];
      }
      segments.push(React.createElement(Text, { key: k++, style }, content));
      rest = rest.slice(pick.m.index + pick.m[0].length);
    }
    return segments;
  }

  function mdToPDF(content) {
    const els = [];
    let k = 0;
    const lines = content.split("\n");
    let i = 0;

    const isTableRow = l =>
      /^\|/.test(l.trim()) && l.trim().lastIndexOf("|") > 0;
    const isSepRow = l => /^\|[\s\-:|]+\|/.test(l.trim());
    const parseRow = l =>
      l
        .trim()
        .replace(/^\||\|$/g, "")
        .split("|")
        .map(c => c.trim());

    while (i < lines.length) {
      const line = lines[i].trim();

      if (!line) {
        i++;
        continue;
      }

      if (/^---+$/.test(line)) {
        els.push(React.createElement(View, { key: k++, style: S.hr }));
        i++;
        continue;
      }

      const hm = line.match(/^(#{1,4})\s+(.*)/);
      if (hm) {
        const st = [S.h1, S.h2, S.h3, S.h4][hm[1].length - 1];
        els.push(
          React.createElement(Text, { key: k++, style: st }, inlineRuns(hm[2]))
        );
        i++;
        continue;
      }

      if (isTableRow(line)) {
        const tableLines = [];
        while (
          i < lines.length &&
          (isTableRow(lines[i].trim()) || isSepRow(lines[i].trim()))
        ) {
          tableLines.push(lines[i]);
          i++;
        }
        const rows = tableLines.filter(l => !isSepRow(l.trim()));
        const [headerRow, ...bodyRows] = rows;
        const headers = parseRow(headerRow);
        const dataRows = bodyRows.map(parseRow);

        const tableEls = [];
        const hCells = headers.map((h, ci) =>
          React.createElement(
            View,
            { key: ci, style: ci < headers.length - 1 ? S.tCell : S.tCellLast },
            React.createElement(Text, { style: S.tHeaderText }, inlineRuns(h))
          )
        );
        tableEls.push(
          React.createElement(
            View,
            { key: "h", style: S.tHeaderRow },
            ...hCells
          )
        );

        dataRows.forEach((row, ri) => {
          const cells = row.map((c, ci) =>
            React.createElement(
              View,
              { key: ci, style: ci < row.length - 1 ? S.tCell : S.tCellLast },
              React.createElement(Text, { style: S.tCellText }, inlineRuns(c))
            )
          );
          tableEls.push(
            React.createElement(
              View,
              { key: `r${ri}`, style: ri % 2 === 0 ? S.tRow : S.tRowAlt },
              ...cells
            )
          );
        });

        els.push(
          React.createElement(View, { key: k++, style: S.table }, ...tableEls)
        );
        continue;
      }

      if (/^[-*]\s/.test(line)) {
        els.push(
          React.createElement(
            Text,
            { key: k++, style: S.li },
            React.createElement(Text, { style: { color: TEAL } }, "• "),
            ...inlineRuns(line.replace(/^[-*]\s+/, ""))
          )
        );
        i++;
        continue;
      }
      if (/^\d+\.\s/.test(line)) {
        const num = line.match(/^(\d+)\./)[1];
        els.push(
          React.createElement(
            Text,
            { key: k++, style: S.li },
            `${num}. `,
            ...inlineRuns(line.replace(/^\d+\.\s+/, ""))
          )
        );
        i++;
        continue;
      }
      els.push(
        React.createElement(Text, { key: k++, style: S.para }, inlineRuns(line))
      );
      i++;
    }
    return els;
  }

  const header = React.createElement(
    View,
    { style: S.header, fixed: true },
    React.createElement(Image, { src: triloLogo, style: S.logo })
  );

  const footer = React.createElement(
    View,
    { style: S.footer, fixed: true },
    React.createElement(
      Text,
      { style: S.footerText },
      `Session: ${sessionDate}`
    ),
    React.createElement(
      Text,
      { style: S.footerText },
      `Exported: ${exportedAt}`
    )
  );

  function makeDraftsBlob(acceptedDrafts) {
    const body = [];
    acceptedDrafts.forEach((d, i) =>
      body.push(
        React.createElement(
          View,
          { key: `d${i}`, style: S.draftCard },
          React.createElement(
            View,
            { style: S.draftRow },
            React.createElement(
              Text,
              { style: S.draftTitle },
              d.title || "Draft"
            ),
            React.createElement(
              Text,
              { style: d.wasEdited ? S.draftTagEdited : S.draftTagAccepted },
              d.wasEdited ? "Edited & Accepted" : "Accepted"
            )
          ),
          ...mdToPDF(d.content || "")
        )
      )
    );
    const doc = React.createElement(
      Document,
      null,
      React.createElement(
        Page,
        { size: "A4", style: S.page },
        header,
        ...body,
        footer
      )
    );
    return pdf(doc).toBlob();
  }

  function makeChatBlob(messages) {
    const body = [];
    messages.forEach((m, i) => {
      if (!m.content) return;
      if (m.role === "user") {
        body.push(
          React.createElement(
            Text,
            { key: `u${i}`, style: S.userQ },
            `Q  ${m.content}`
          )
        );
      } else if (m.role === "assistant") {
        const draftEls = (m.drafts || []).map((draft, di) => {
          const st = draftStates[`${i}-${di}`];
          const content = st?.content ?? draft.content;
          const wasEdited = st?.wasEdited ?? false;
          const status = st?.status ?? "pending";
          const tag =
            status === "accepted"
              ? React.createElement(
                  Text,
                  { style: wasEdited ? S.draftTagEdited : S.draftTagAccepted },
                  wasEdited ? "Edited & Accepted" : "Accepted"
                )
              : null;
          return React.createElement(
            View,
            { key: `draft${di}`, style: S.draftCard },
            React.createElement(
              View,
              { style: S.draftRow },
              React.createElement(Text, { style: S.draftTitle }, draft.title),
              tag
            ),
            React.createElement(
              Text,
              { style: S.draftContent },
              ...inlineRuns(content)
            )
          );
        });
        body.push(
          React.createElement(
            View,
            { key: `a${i}`, style: S.asstBlock },
            ...mdToPDF(m.content),
            ...draftEls
          )
        );
      }
    });
    const doc = React.createElement(
      Document,
      null,
      React.createElement(
        Page,
        { size: "A4", style: S.page },
        header,
        ...body,
        footer
      )
    );
    return pdf(doc).toBlob();
  }

  return { makeDraftsBlob, makeChatBlob };
}

function DocxIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function DraftCard({ draft, draftKey, draftStates, setDraftStates }) {
  const state = draftStates[draftKey] || {
    status: "pending",
    content: draft.content,
    title: draft.title,
  };
  const [editText, setEditText] = React.useState(draft.content);
  const isEditing = state.status === "editing";

  const set = (status, content) => {
    const newContent = content ?? state.content;
    setDraftStates(prev => ({
      ...prev,
      [draftKey]: {
        status,
        content: newContent,
        title: draft.title,
        wasEdited:
          status === "accepted" &&
          content !== undefined &&
          content !== draft.content,
      },
    }));
  };

  return (
    <div
      className={`trilochat__draft-card trilochat__draft-card--${state.status}`}>
      <div className="trilochat__draft-card-header">
        <span>{draft.title}</span>
        {state.status === "accepted" && (
          <span className="trilochat__draft-card-status trilochat__draft-card-status--accepted">
            Accepted
          </span>
        )}
      </div>

      {/* TODO: number verification disabled — needs refinement
      {draft._unverified_numbers && draft._unverified_numbers.length > 0 && (
        <div className="trilochat__draft-card-warning">
          <span>⚠</span>
          <span>
            The following numbers could not be verified against source data:{" "}
            <strong>{draft._unverified_numbers.join(", ")}</strong>. Check before using.
          </span>
        </div>
      )} */}

      {isEditing ? (
        <textarea
          className="trilochat__draft-card-editor"
          value={editText}
          onChange={e => setEditText(e.target.value)}
        />
      ) : (
        <div className="trilochat__draft-card-content">{state.content}</div>
      )}

      <div className="trilochat__draft-card-actions">
        {state.status === "pending" && (
          <>
            <button
              className="trilochat__draft-card-btn trilochat__draft-card-btn--accept"
              onClick={() => set("accepted")}>
              Accept
            </button>
            <button
              className="trilochat__draft-card-btn"
              onClick={() => {
                setEditText(draft.content);
                set("editing");
              }}>
              Edit
            </button>
          </>
        )}
        {isEditing && (
          <>
            <button
              className="trilochat__draft-card-btn trilochat__draft-card-btn--save"
              onClick={() => set("accepted", editText)}>
              Save & Accept
            </button>
            <button
              className="trilochat__draft-card-btn"
              onClick={() => set("pending")}>
              Cancel
            </button>
          </>
        )}
        {state.status === "accepted" && (
          <button
            className="trilochat__draft-card-btn"
            onClick={() => {
              setEditText(state.content);
              set("editing");
            }}>
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

function DocPanel({ contentId, sessionId }) {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(
          `${API_URL}/chat/sessions/${sessionId}/content/${contentId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("not found");
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError("Could not load document content.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contentId, sessionId, getAccessTokenSilently]);

  if (loading)
    return (
      <div className="trilochat__doc-panel trilochat__doc-panel--loading">
        Loading document…
      </div>
    );
  if (error)
    return (
      <div className="trilochat__doc-panel trilochat__doc-panel--error">
        {error}
      </div>
    );
  return (
    <div className="trilochat__doc-panel">
      {data.title && (
        <div className="trilochat__doc-panel__title">{data.title}</div>
      )}
      <div
        className="trilochat__doc-panel__content"
        dangerouslySetInnerHTML={{ __html: renderMarkdown(data.content) }}
      />
    </div>
  );
}

function renderMessageContent(content, sessionId) {
  const DOC_RE = /\[DOC:([a-f0-9]+)\]/g;
  const parts = [];
  let last = 0;
  let match;
  while ((match = DOC_RE.exec(content)) !== null) {
    if (match.index > last) {
      const html = renderMarkdown(content.slice(last, match.index));
      parts.push(<div key={last} dangerouslySetInnerHTML={{ __html: html }} />);
    }
    parts.push(
      <DocPanel key={match[1]} contentId={match[1]} sessionId={sessionId} />
    );
    last = match.index + match[0].length;
  }
  if (last < content.length) {
    const html = renderMarkdown(content.slice(last));
    parts.push(<div key={last} dangerouslySetInnerHTML={{ __html: html }} />);
  }
  return parts.length ? (
    parts
  ) : (
    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }} />
  );
}

function HelpPanel({ onClose }) {
  return (
    <div className="trilochat__help-overlay" onClick={onClose}>
      <div className="trilochat__help-panel" onClick={e => e.stopPropagation()}>
        <div className="trilochat__help-header">
          <h2>How to use TriloChat</h2>
          <button
            className="trilochat__help-close"
            onClick={onClose}
            aria-label="Close">
            ✕
          </button>
        </div>
        <div className="trilochat__help-body">
          <p className="trilochat__help-intro">
            You never need to provide study IDs or technical details — just use
            the study name as you know it.
          </p>

          <div className="trilochat__help-section">
            <h3>Labels</h3>
            <div className="trilochat__help-labels">
              <div className="trilochat__help-label">
                <span className="trilochat__help-label-tag trilochat__help-label-tag--data">
                  [DATA]
                </span>
                <span>
                  Numbers and findings from the study rules engine. Fixed — you
                  can rely on these.
                </span>
              </div>
              <div className="trilochat__help-label">
                <span className="trilochat__help-label-tag trilochat__help-label-tag--assessment">
                  [ASSESSMENT]
                </span>
                <span>
                  The assistant&apos;s clinical interpretation. Requires your
                  review before acting on it.
                </span>
              </div>
              <div className="trilochat__help-label">
                <span className="trilochat__help-label-tag trilochat__help-label-tag--draft">
                  [DRAFT]
                </span>
                <span>Text ready for the CSR, shown for your approval.</span>
              </div>
            </div>
          </div>

          <div className="trilochat__help-section">
            <h3>Example prompts</h3>
            <div className="trilochat__help-examples">
              {[
                [
                  "Overview",
                  [
                    "What studies do I have?",
                    "Show me the status of FAB DRUG DEMO STUDY",
                    "Which sections have errors in my study?",
                  ],
                ],
                [
                  "Protocol Deviations",
                  [
                    "Review the protocol deviations for [study name]",
                    "Show me all the decisions recorded so far",
                  ],
                ],
                [
                  "Demographics",
                  [
                    "Review the demographics for [study name]",
                    "The clinical team confirmed the BMI difference is not relevant — record that",
                  ],
                ],
                [
                  "Adverse Events",
                  ["Review the adverse events for [study name]"],
                ],
                [
                  "Efficacy",
                  ["Review the secondary efficacy for [study name]"],
                ],
                [
                  "Documents & Sections",
                  [
                    "What documents have been uploaded for [study name]?",
                    "Show me the Introduction section of [study name]",
                    "Why does the serious adverse events section show what it shows?",
                  ],
                ],
                [
                  "Comparing studies",
                  [
                    "Compare the wizard answers between [study A] and [study B]",
                  ],
                ],
              ].map(([heading, items]) => (
                <div key={heading} className="trilochat__help-example-group">
                  <h4>{heading}</h4>
                  <ul>
                    {items.map(q => (
                      <li key={q}>
                        <em>{q}</em>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="trilochat__help-section">
            <h3>Drafts & export</h3>
            <p>
              When TriloChat composes a passage ready for your CSR, it appears
              as a draft card below the message. You can <strong>Accept</strong>{" "}
              it or <strong>Edit</strong> it. Use <strong>Export report</strong>{" "}
              in the header to download a DOCX with all accepted drafts and the
              conversation findings.
            </p>
            <p>
              Every number in a draft is automatically checked against the data
              returned during the session. If a number cannot be verified, the
              draft card shows a warning listing the specific values to check
              before use.
            </p>
          </div>

          <div className="trilochat__help-section trilochat__help-section--limits">
            <h3>What TriloChat will not do</h3>
            <ul>
              <li>
                Conclude that any protocol deviation, adverse event, SAE, or
                death is unrelated to the study drug — that determination
                belongs to the clinical team
              </li>
              <li>
                Insert the standard regulatory sentence without your explicit
                instruction that the clinical team has confirmed the decision
              </li>
              <li>
                Use a study ID in any response — studies are always referred to
                by name
              </li>
              <li>
                Mix numbers from different studies without attributing each to
                its source study by name
              </li>
              <li>Rephrase or summarise regulated section content</li>
              <li>
                Answer questions outside clinical trial reporting — it will
                redirect to study review
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

const TOOL_LABELS = {
  find_study: "Finding study",
  list_studies: "Listing studies",
  get_study_details: "Fetching study details",
  get_request_status: "Checking study status",
  list_sections: "Checking sections",
  explain_section_output: "Explaining section output",
  explain_issues: "Checking issues",
  show_section: "Reading section",
  read_document: "Reading document",
  compare_wizard_answers: "Comparing study answers",
  list_documents: "Listing documents",
  list_protocol_deviations: "Checking protocol deviations",
  assess_deviation: "Assessing deviation",
  record_deviation_decision: "Recording decision",
  get_deviation_decisions: "Loading decisions",
  list_demography_differences: "Checking demographics",
  assess_demography_difference: "Assessing demographic difference",
  record_demography_decision: "Recording decision",
  get_demography_decisions: "Loading decisions",
  review_secondary_efficacy: "Reviewing efficacy data",
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / 86400000);
  if (diffDays === 0)
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function ChatIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <path d="M21 15c0 .53-.21 1.04-.59 1.41-.37.38-.88.59-1.41.59H7l-4 4V5c0-.53.21-1.04.59-1.41C3.96 3.21 4.47 3 5 3h14c.53 0 1.04.21 1.41.59.38.37.59.88.59 1.41v10z" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4h6v2" />
    </svg>
  );
}

export default function TriloChat() {
  const { getAccessTokenSilently } = useAuth0();

  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeToolCalls, setActiveToolCalls] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [studies, setStudies] = useState([]);
  const [selectedStudyId, setSelectedStudyId] = useState("");
  const [draftStates, setDraftStates] = useState({});
  const [showHelp, setShowHelp] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const draftSessionRef = useRef(null);

  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  // Keep a ref in sync with activeSessionId so the save effect can read it
  // without having activeSessionId as a dependency (which would cause it to fire
  // on session switch before the restore effect runs, overwriting saved data).
  useEffect(() => {
    draftSessionRef.current = activeSessionId;
  }, [activeSessionId]);

  // Restore draft states from localStorage when session changes
  useEffect(() => {
    if (!activeSessionId) {
      setDraftStates({});
      return;
    }
    try {
      const saved = localStorage.getItem(`trilochat_drafts_${activeSessionId}`);
      setDraftStates(saved ? JSON.parse(saved) : {});
    } catch {
      setDraftStates({});
    }
  }, [activeSessionId]);

  // Persist draft states — depends only on draftStates, reads session from ref
  // so session switches don't trigger a save with stale data.
  useEffect(() => {
    const sid = draftSessionRef.current;
    if (!sid) return;
    localStorage.setItem(
      `trilochat_drafts_${sid}`,
      JSON.stringify(draftStates)
    );
  }, [draftStates]); // eslint-disable-line

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeToolCalls, scrollToBottom]);

  // Load sessions and studies on mount
  useEffect(() => {
    CsrAPI.getAllStudies()
      .then(docs => setStudies((docs || []).filter(d => d.id && d.name)))
      .catch(() => {});

    CsrAPI.listChatSessions()
      .then(data => {
        const sorted = (data.sessions || []).sort(
          (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
        );
        setSessions(sorted);
      })
      .catch(() => {})
      .finally(() => setLoadingSessions(false));
  }, []);

  const selectSession = useCallback(sessionId => {
    setActiveSessionId(sessionId);
    setActiveToolCalls([]);
    CsrAPI.getChatSession({ sessionId }).then(data => {
      setActiveSession(data);
      setSelectedStudyId(data.study_id || "");
      setMessages(
        (data.messages || []).map(m => ({
          role: m.role,
          content:
            typeof m.content === "string"
              ? m.content
              : JSON.stringify(m.content),
          options: m.options || [],
          drafts: m.drafts || [],
        }))
      );
    });
  }, []);

  const handleStudyChange = useCallback(
    async studyId => {
      setSelectedStudyId(studyId);
      if (activeSessionId) {
        try {
          const updated = await CsrAPI.updateChatSession({
            sessionId: activeSessionId,
            studyId,
          });
          setActiveSession(prev => ({ ...prev, study_id: updated.study_id }));
        } catch {
          // silent — dropdown already updated locally
        }
      }
    },
    [activeSessionId]
  );

  const createNewSession = useCallback(async studyId => {
    try {
      const session = await CsrAPI.createChatSession({
        studyId: studyId || null,
      });
      setSessions(prev => [session, ...prev]);
      setActiveSessionId(session.session_id);
      setActiveSession(session);
      setSelectedStudyId("");
      setMessages([]);
      setActiveToolCalls([]);
      textareaRef.current?.focus();
    } catch {
      // session creation failed silently; user can retry
    }
  }, []);

  const deleteSession = useCallback(
    async (e, sessionId) => {
      e.stopPropagation();
      try {
        await CsrAPI.deleteChatSession({ sessionId });
        setSessions(prev => prev.filter(s => s.session_id !== sessionId));
        if (activeSessionId === sessionId) {
          setActiveSessionId(null);
          setActiveSession(null);
          setMessages([]);
          setActiveToolCalls([]);
        }
      } catch {
        // deletion failed silently
      }
    },
    [activeSessionId]
  );

  const exportDrafts = useCallback(async () => {
    if (!activeSessionId || messages.length === 0 || isExporting) return;
    const acceptedDrafts = Object.values(draftStates).filter(
      d => d.status === "accepted"
    );
    const slug = (activeSession?.title || "TriloChat")
      .replace(/[^a-z0-9]/gi, "_")
      .slice(0, 50);
    setIsExporting(true);
    try {
      const sessionDate = fmtDate(activeSession?.created_at);
      const exportedAt = fmtDate(new Date().toISOString());
      const { makeDraftsBlob, makeChatBlob } = await buildPDFAssets(
        sessionDate,
        exportedAt,
        draftStates
      );

      const triggerDownload = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      };

      const chatBlob = await makeChatBlob(messages);
      triggerDownload(chatBlob, `${slug}_chat.pdf`);

      if (acceptedDrafts.length > 0) {
        const draftsBlob = await makeDraftsBlob(acceptedDrafts);
        triggerDownload(draftsBlob, `${slug}_accepted_text.pdf`);
      }
    } catch (e) {
      console.error("PDF export failed", e);
    } finally {
      setIsExporting(false);
    }
  }, [activeSessionId, activeSession, messages, draftStates, isExporting]);

  const sendMessage = useCallback(
    async overrideText => {
      const text = (overrideText !== undefined ? overrideText : input).trim();
      if (!text || isStreaming) return;

      let sessionId = activeSessionId;

      // Auto-create a session if none is selected
      if (!sessionId) {
        try {
          const session = await CsrAPI.createChatSession({
            studyId: selectedStudyId || null,
          });
          setSessions(prev => [session, ...prev]);
          setActiveSessionId(session.session_id);
          setActiveSession(session);
          sessionId = session.session_id;
        } catch {
          return;
        }
      }

      const userMessage = { role: "user", content: text };
      setMessages(prev => [...prev, userMessage]);
      if (overrideText === undefined) setInput("");
      setIsStreaming(true);
      setActiveToolCalls([]);

      // Auto-resize textarea back down
      if (textareaRef.current) {
        textareaRef.current.style.height = "44px";
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${API_URL}/chat/sessions/${sessionId}/messages`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ message: text }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        // Poll for the result — background Lambda processes the message asynchronously
        const pollStart = Date.now();
        const MAX_POLL_MS = 10 * 60 * 1000; // 10 minutes

        pollIntervalRef.current = setInterval(async () => {
          // Guard: if already handled (ref nulled), bail — multiple in-flight ticks can all see "complete"
          if (!pollIntervalRef.current) return;

          if (Date.now() - pollStart > MAX_POLL_MS) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            setMessages(prev => [
              ...prev,
              {
                role: "error",
                content: "The request timed out. Please try again.",
              },
            ]);
            setIsStreaming(false);
            setActiveToolCalls([]);
            return;
          }

          try {
            const pollToken = await getAccessTokenSilently();
            const statusRes = await fetch(
              `${API_URL}/chat/sessions/${sessionId}/messages/status`,
              {
                headers: { Authorization: `Bearer ${pollToken}` },
              }
            );
            if (!statusRes.ok) return; // transient error — keep polling

            const data = await statusRes.json();

            if (data.status === "complete") {
              if (!pollIntervalRef.current) return; // another tick already handled this
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              const msgs = data.messages || [];
              const last = msgs[msgs.length - 1];
              if (last && last.role === "assistant" && last.content) {
                setMessages(prev => [
                  ...prev,
                  {
                    role: "assistant",
                    content: last.content,
                    options: last.options || [],
                    drafts: last.drafts || [],
                  },
                ]);
                setSessions(prev =>
                  prev.map(s =>
                    s.session_id === sessionId
                      ? {
                          ...s,
                          title: s.title || text.slice(0, 60),
                          updated_at: new Date().toISOString(),
                        }
                      : s
                  )
                );
              }
              setIsStreaming(false);
              setActiveToolCalls([]);
            } else if (data.status === "error") {
              if (!pollIntervalRef.current) return;
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
              setMessages(prev => [
                ...prev,
                {
                  role: "error",
                  content:
                    data.message || "Something went wrong. Please try again.",
                },
              ]);
              setIsStreaming(false);
              setActiveToolCalls([]);
            }
            // "pending" or "idle" → keep polling
          } catch {
            // transient network error — keep polling
          }
        }, 2000);
      } catch {
        setMessages(prev => [
          ...prev,
          { role: "error", content: "Something went wrong. Please try again." },
        ]);
        setIsStreaming(false);
        setActiveToolCalls([]);
      }
    },
    [
      input,
      isStreaming,
      activeSessionId,
      selectedStudyId,
      getAccessTokenSilently,
    ]
  );

  const lastMsg =
    !isStreaming && messages.length > 0 ? messages[messages.length - 1] : null;
  const quickReplies =
    lastMsg?.role === "assistant" ? lastMsg.options || [] : [];

  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    },
    [sendMessage]
  );

  const handleTextareaChange = useCallback(e => {
    setInput(e.target.value);
    // Auto-grow textarea
    const el = e.target;
    el.style.height = "44px";
    el.style.height = Math.min(el.scrollHeight, 160) + "px";
  }, []);

  const pageContent = (
    <div className="trilochat">
      {/* ── Sessions sidebar ── */}
      <div className="trilochat__sessions">
        <div className="trilochat__sessions-header">
          <h3>TriloChat</h3>
          <div className="trilochat__sessions-header-actions">
            <button
              className="trilochat__help-btn"
              onClick={() => setShowHelp(true)}
              aria-label="Help">
              ?
            </button>
            <button
              className="trilochat__new-btn"
              onClick={() => createNewSession(null)}>
              <PlusIcon />
              New chat
            </button>
          </div>
        </div>
        {showHelp && <HelpPanel onClose={() => setShowHelp(false)} />}
        <div className="trilochat__sessions-list">
          {loadingSessions ? null : sessions.length === 0 ? (
            <p className="trilochat__sessions-empty">No conversations yet</p>
          ) : (
            sessions.map(s => (
              <button
                key={s.session_id}
                className={`trilochat__session-item ${s.session_id === activeSessionId ? "trilochat__session-item--active" : ""}`}
                onClick={() => selectSession(s.session_id)}>
                <div className="trilochat__session-info">
                  <div className="trilochat__session-title">
                    {s.title || "New chat"}
                  </div>
                  <div className="trilochat__session-time">
                    {formatTime(s.updated_at)}
                  </div>
                </div>
                <span
                  className="trilochat__session-delete"
                  role="button"
                  aria-label="Delete chat"
                  onClick={e => deleteSession(e, s.session_id)}>
                  <TrashIcon />
                </span>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── Main chat area ── */}
      <div className="trilochat__main">
        {!activeSessionId ? (
          <div className="trilochat__no-session">
            <ChatIcon />
            <h3>Ask TriloChat anything</h3>
            <p>
              Ask about your study data, section outputs, protocol deviations,
              demographics, or efficacy results. TriloChat uses your real study
              data to answer.
            </p>
            {studies.length > 0 && (
              <div className="trilochat__study-selector">
                <label className="trilochat__study-label">Study context</label>
                <select
                  className="trilochat__study-select"
                  value={selectedStudyId}
                  onChange={e => setSelectedStudyId(e.target.value)}>
                  <option value="">No study context</option>
                  {studies.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button onClick={() => createNewSession(selectedStudyId)}>
              Start a conversation
            </button>
          </div>
        ) : (
          <>
            <div className="trilochat__chat-header">
              <h2>{activeSession?.title || "New chat"}</h2>
              <div className="trilochat__chat-header-right">
                {messages.length > 0 && (
                  <button
                    className="trilochat__export-btn"
                    onClick={exportDrafts}
                    disabled={isExporting}>
                    <DocxIcon /> {isExporting ? "Generating…" : "Export PDF"}
                  </button>
                )}
                {studies.length > 0 && (
                  <div className="trilochat__study-selector">
                    <label className="trilochat__study-label">Study</label>
                    <select
                      className="trilochat__study-select"
                      value={selectedStudyId}
                      onChange={e => handleStudyChange(e.target.value)}>
                      <option value="">No study context</option>
                      {studies.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="trilochat__messages">
              {messages.length === 0 && !isStreaming && (
                <div className="trilochat__empty-state">
                  <ChatIcon />
                  <h3>Ready to help</h3>
                  <p>
                    Try asking &ldquo;list my studies&rdquo;, &ldquo;show me
                    demographics for [study name]&rdquo;, or &ldquo;explain why
                    section X is failing&rdquo;.
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`trilochat__message trilochat__message--${msg.role}`}>
                  <div
                    className={`trilochat__avatar trilochat__avatar--${msg.role === "user" ? "user" : "assistant"}`}>
                    {msg.role === "user" ? "You" : "TC"}
                  </div>
                  <div
                    className={`trilochat__bubble${msg.drafts?.length ? " trilochat__bubble--has-drafts" : ""}`}>
                    {msg.role === "assistant" ? (
                      <>
                        {msg.content &&
                          renderMessageContent(msg.content, activeSessionId)}
                        {msg.drafts?.map((draft, di) => {
                          const draftKey = `${i}-${di}`;
                          return (
                            <DraftCard
                              key={draftKey}
                              draft={draft}
                              draftKey={draftKey}
                              draftStates={draftStates}
                              setDraftStates={setDraftStates}
                            />
                          );
                        })}
                      </>
                    ) : (
                      <span style={{ whiteSpace: "pre-wrap" }}>
                        {msg.content}
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {activeToolCalls.length > 0 && (
                <div className="trilochat__thinking">
                  {activeToolCalls.map((name, i) => (
                    <span key={i} className="trilochat__tool-pill">
                      <span className="trilochat__tool-dot" />
                      {TOOL_LABELS[name] || name}…
                    </span>
                  ))}
                </div>
              )}

              {isStreaming && activeToolCalls.length === 0 && (
                <div className="trilochat__message trilochat__message--assistant">
                  <div className="trilochat__avatar trilochat__avatar--assistant">
                    TC
                  </div>
                  <div className="trilochat__bubble trilochat__bubble--typing">
                    <span className="trilochat__typing-dot" />
                    <span className="trilochat__typing-dot" />
                    <span className="trilochat__typing-dot" />
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {quickReplies.length > 0 && (
              <div className="trilochat__quick-replies">
                {quickReplies.map((opt, i) => (
                  <button
                    key={i}
                    className="trilochat__quick-reply"
                    onClick={() => sendMessage(opt)}>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            <div className="trilochat__input-area">
              <div className="trilochat__input-row">
                <textarea
                  ref={textareaRef}
                  className="trilochat__textarea"
                  rows={1}
                  placeholder="Ask anything about your studies…"
                  value={input}
                  onChange={handleTextareaChange}
                  onKeyDown={handleKeyDown}
                  disabled={isStreaming}
                />
                <button
                  className="trilochat__send-btn"
                  onClick={sendMessage}
                  disabled={!input.trim() || isStreaming}
                  aria-label="Send message">
                  <SendIcon />
                </button>
              </div>
              <p className="trilochat__hint">
                Enter to send · Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <DashboardContent
      sidebarCSR={false}
      headerTitle=""
      headerButton={false}
      pageContent={pageContent}
    />
  );
}
